import * as React from "react";
import { useRouter } from "@tanstack/react-router";
import { MonitorPlay, AlertTriangle, Info } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { DetailPageLayout } from "../layouts/DetailPageLayout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { useSectionCollapse } from "../../hooks/useSectionCollapse";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchAnalyticsSessionDetail,
  fetchAnalyticsSessionReplay,
  type AnalyticsSessionDetail,
  type AnalyticsReplayChunk,
} from "../../lib/api/product-analytics";
import { ReplayPlayer } from "./ReplayPlayer";
import { UserCell, OrganizationCell } from "./productAnalyticsEntityCells";
import { vars } from "../../../ui/styles/theme.css";
import * as s from "./ProductAnalyticsDetail.css";

const REPLAY_SECTION_ID = "replay";
const ERRORS_SECTION_ID = "linked-errors";
const CONTEXT_SECTION_ID = "context";
const COLLAPSE_SCOPE = "product-analytics-session";

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** 30-day hard retention — recordings older than this are gone, no soft-delete/grace-period. */
const RETENTION_DAYS = 30;

function isExpired(startedAt: string): boolean {
  const started = new Date(startedAt).getTime();
  if (Number.isNaN(started)) return false;
  return Date.now() - started > RETENTION_DAYS * 24 * 60 * 60 * 1000;
}

export function ProductAnalyticsSessionDetailPage({ sessionId }: { sessionId: string }) {
  const router = useRouter();
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const { isExpanded, toggle, bindSectionLayout } = useSectionCollapse(COLLAPSE_SCOPE);

  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [detail, setDetail] = React.useState<AnalyticsSessionDetail | null>(null);
  const [chunks, setChunks] = React.useState<AnalyticsReplayChunk[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    (async () => {
      try {
        const [detailResult, replayResult] = await Promise.allSettled([
          fetchAnalyticsSessionDetail(token, sessionId),
          fetchAnalyticsSessionReplay(token, sessionId),
        ]);
        if (cancelled) return;

        if (detailResult.status === "fulfilled") {
          setDetail(detailResult.value);
        } else {
          setLoadError(
            detailResult.reason instanceof Error
              ? detailResult.reason.message
              : t("pro.system.productAnalytics.session.loadError"),
          );
        }
        // Replay can legitimately 404 (aged out past retention) without
        // that being a page-level error — the header badge covers it.
        setChunks(replayResult.status === "fulfilled" ? replayResult.value : []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, sessionId, t]);

  const sectionLayout = bindSectionLayout([
    { id: CONTEXT_SECTION_ID, label: t("pro.system.productAnalytics.session.context"), icon: <Info size={16} /> },
    { id: REPLAY_SECTION_ID, label: t("pro.system.productAnalytics.session.replay"), icon: <MonitorPlay size={16} /> },
    {
      id: ERRORS_SECTION_ID,
      label: t("pro.system.productAnalytics.session.linkedErrors"),
      icon: <AlertTriangle size={16} />,
    },
  ]);

  if (!token) {
    return (
      <div className={s.page}>
        <Callout variant="error">{t("pro.system.productAnalytics.signInRequired")}</Callout>
      </div>
    );
  }

  if (loading) {
    return <AuthTransitionScreen variant="fill" />;
  }

  if (loadError || !detail) {
    return (
      <div className={s.page}>
        <Callout variant="error">
          {loadError ?? t("pro.system.productAnalytics.session.notFound")}
        </Callout>
      </div>
    );
  }

  const { session, linkedErrors } = detail;
  const expired = session.startedAt ? isExpired(session.startedAt) : false;
  const chunkUrls = (chunks ?? []).map((c) => c.url);

  return (
    <DetailPageLayout
      backLink={{
        label: t("pro.system.productAnalytics.session.backToSessions"),
        onClick: () => router.history.back(),
      }}
      header={{
        media: <MonitorPlay size={28} color={vars.color.fgMuted} />,
        title: formatTime(session.startedAt),
        subtitle: session.urlPath ?? undefined,
        badges: [
          <span key="appSource" className={s.badge}>
            {session.appSource === "smart-web"
              ? t("pro.system.productAnalytics.appSource.smartWeb")
              : t("pro.system.productAnalytics.appSource.proWeb")}
          </span>,
          session.hasErrors ? (
            <span key="hasErrors" className={s.badgeDanger}>
              {t("pro.system.productAnalytics.hasErrors.yes")}
            </span>
          ) : null,
        ].filter(Boolean),
      }}
      {...sectionLayout}
    >
      <CollapsibleSection
        id={CONTEXT_SECTION_ID}
        title={t("pro.system.productAnalytics.session.context")}
        icon={<Info size={18} />}
        expanded={isExpanded(CONTEXT_SECTION_ID)}
        onToggleExpanded={() => toggle(CONTEXT_SECTION_ID)}
        compactHeader
      >
        <div className={s.contextGrid}>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.appSource")}</span>
            <span className={s.contextValue}>{session.appSource}</span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.user")}</span>
            <span className={s.contextValue}>
              <UserCell userId={session.userId} accessToken={token} />
            </span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.col.organization")}</span>
            <span className={s.contextValue}>
              <OrganizationCell organizationId={session.organizationId} accessToken={token} />
            </span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.session.userAgent")}</span>
            <span className={s.contextValue}>{session.userAgent ?? "—"}</span>
          </div>
          <div className={s.contextField}>
            <span className={s.contextLabel}>{t("pro.system.productAnalytics.session.appVersion")}</span>
            <span className={s.contextValue}>{session.appVersion ?? "—"}</span>
          </div>
        </div>
        <Text level="body-small" style={{ color: vars.color.fgMuted, marginTop: 12, display: "block" }}>
          {t("pro.system.productAnalytics.session.contextNote")}
        </Text>
      </CollapsibleSection>

      <CollapsibleSection
        id={REPLAY_SECTION_ID}
        title={t("pro.system.productAnalytics.session.replay")}
        icon={<MonitorPlay size={18} />}
        expanded={isExpanded(REPLAY_SECTION_ID)}
        onToggleExpanded={() => toggle(REPLAY_SECTION_ID)}
        compactHeader
        flushBody
      >
        {expired || chunkUrls.length === 0 ? (
          <div style={{ padding: 16 }}>
            <Callout variant="info">
              {expired
                ? t("pro.system.productAnalytics.session.expired")
                : t("pro.system.productAnalytics.replay.empty")}
            </Callout>
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <Text level="body-small" style={{ color: vars.color.fgMuted, marginBottom: 8, display: "block" }}>
              {t("pro.system.productAnalytics.session.masked")}
            </Text>
            <ReplayPlayer chunkUrls={chunkUrls} accessToken={token} />
          </div>
        )}
      </CollapsibleSection>

      <CollapsibleSection
        id={ERRORS_SECTION_ID}
        title={t("pro.system.productAnalytics.session.linkedErrors")}
        subtitle={String(linkedErrors.length)}
        icon={<AlertTriangle size={18} />}
        expanded={isExpanded(ERRORS_SECTION_ID)}
        onToggleExpanded={() => toggle(ERRORS_SECTION_ID)}
        compactHeader
      >
        {linkedErrors.length === 0 ? (
          <Text level="body-small" style={{ color: vars.color.fgMuted }}>
            {t("pro.system.productAnalytics.session.noLinkedErrors")}
          </Text>
        ) : (
          <div className={s.errorList}>
            {linkedErrors.map((err) => (
              <button
                key={err.id}
                type="button"
                className={s.errorCard}
                onClick={() =>
                  router.navigate({
                    to: "/system/product-analytics/errors/$errorId",
                    params: { errorId: err.id },
                  })
                }
              >
                <span className={s.errorCardTime}>{formatTime(err.occurredAt)}</span>
                <span className={s.errorCardMessage}>{err.message}</span>
                <span className={s.errorCardSeverity} data-severity={err.severity}>
                  {err.severity}
                </span>
              </button>
            ))}
          </div>
        )}
      </CollapsibleSection>
    </DetailPageLayout>
  );
}
